import { z } from "zod";

/**
 * GÜNLÜK ÖZET / WORKFLOW KARAR DESTEĞİ (CLOS-290 `[194]` · CLOS-1021/1022).
 *
 * ### ⚠ ÖZET BİR GÖREV LİSTESİ DEĞİL
 *
 * Kart: *"Ana Sayfa AI özeti ... KESİN GÖREV, hukuki görüş veya hukuki yükümlülük gibi
 * sunulmaz."* Bu yüzden şemada bir "yapılacak", "atama" ya da "son tarih" alanı yok:
 * `focus` bir DİKKAT ÖNERİSİ ve her satır zaten var olan bir kaynağa (bekleyen iş,
 * kritik tarih, sözleşme) bağlanıyor.
 *
 * ### ⚠ HER SATIR EN DAR KAYNAĞA BAĞLI
 *
 * Kart: *"Her özet ve öneri mümkün olan en dar kaynak sözleşme, değişmez versiyon, madde,
 * görev, olay veya tarihe bağlanır."* `sourceKind` + `sourceLabel` bunun karşılığı:
 * kullanıcı özetten kaynağa gidebilmeli. Kaynağı olmayan bir "bugün şuna bak" cümlesi,
 * doğrulanamaz bir iddiadır.
 */

export const AI_BRIEFING_SOURCE_KINDS = [
  "pending_task",
  "upcoming_date",
  "recent_contract",
  "alert",
] as const;
export type AiBriefingSourceKind = (typeof AI_BRIEFING_SOURCE_KINDS)[number];

export const AI_BRIEFING_SOURCE_KIND_NAMES: Record<AiBriefingSourceKind, string> = {
  pending_task: "Bekleyen iş",
  upcoming_date: "Kritik tarih",
  recent_contract: "Sözleşme",
  alert: "Uyarı",
};

export const AI_BRIEFING_PRIORITIES = ["high", "medium", "low"] as const;
export type AiBriefingPriority = (typeof AI_BRIEFING_PRIORITIES)[number];

export const AI_BRIEFING_PRIORITY_NAMES: Record<AiBriefingPriority, string> = {
  high: "Önce buna bakın",
  medium: "Bugün içinde",
  low: "Bilgi amaçlı",
};

/** ⚠ EKRANDA ZORUNLU: özet bir görev ataması ya da hukuki yükümlülük değil. */
export const AI_BRIEFING_ADVISORY =
  "Bu özet bir görev ataması, süre hesabı ya da hukuki yükümlülük bildirimi değildir; " +
  "gününüzü planlamanıza yardımcı olmak için hazırlanmıştır.";

export const AI_BRIEFING_EMPTY_MESSAGE =
  "Bugün için öne çıkan bir başlık çıkmadı. Bu, bekleyen işiniz olmadığı anlamına gelmez.";

/** ⚠ Veri kaynağı olmayan bölümün cümlesi (kartın "eksik/uygulanamaz" davranışı). */
export const AI_BRIEFING_NO_SOURCE_MESSAGE =
  "Bugün için özetlenecek bir bekleyen iş, kritik tarih ya da sözleşme hareketi yok.";

export const aiBriefingFocusSchema = z.strictObject({
  priority: z.enum(AI_BRIEFING_PRIORITIES),
  title: z.string().min(1).max(200),
  rationale: z.string().min(1).max(800),
  /** ⚠ Kaynağın TÜRÜ ve kullanıcının ekranda göreceği ETİKETİ — kimlik değil. */
  sourceKind: z.enum(AI_BRIEFING_SOURCE_KINDS),
  sourceLabel: z.string().min(1).max(300),
});
export type AiBriefingFocus = z.infer<typeof aiBriefingFocusSchema>;

export const aiBriefingOutputSchema = z.strictObject({
  /** Günün bir cümlelik özeti. */
  summary: z.string().max(1000).default(""),
  focus: z.array(aiBriefingFocusSchema).max(10),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(10).default([]),
});
export type AiBriefingOutput = z.infer<typeof aiBriefingOutputSchema>;

/**
 * ⚠ MODELİN GÖSTERDİĞİ KAYNAK GERÇEKTEN VAR MI. Sunucu, özetin her satırındaki
 * `sourceLabel`'ı projeksiyona verilen etiketlerle karşılaştırıyor: eşleşmeyen satır
 * "kaynağı doğrulanamadı" diye işaretleniyor. Kartın *"başka domainlerden tahminle veri
 * uydurmaz"* kriterinin ölçülebilir hâli bu.
 */
export type AiBriefingFocusView = AiBriefingFocus & {
  sourceVerified: boolean;
  /**
   * ⚠ AI OTOMATİK BİR İŞLEM YAPILDIĞINI İMA ETTİ Mİ (CLOS-1089 *"no-auto-action"*).
   * Günlük özet dikkat çekmeli, yapılmış bir iş bildirmemeli; işaretleniyor, silinmiyor.
   */
  certaintyFlagged: boolean;
};

export const AI_BRIEFING_UNVERIFIED_WARNING =
  "Bu satırın işaret ettiği kaynak bugünkü listenizde bulunamadı; doğrulamadan işlem yapmayın.";

export type AiBriefingRunSummary = {
  id: string;
  status: "completed" | "failed";
  summary: string;
  focus: AiBriefingFocusView[];
  reviewNeeded: string[];
  errorCode: "provider_error" | "invalid_output" | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  /** Kaç kaynak satırı özetlendi — kapsamın ölçüsü. */
  sourceCount: number;
  createdAt: string;
};

export const AI_BRIEFING_ERROR_MESSAGES: Record<"provider_error" | "invalid_output", string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; özet üretilemedi ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

/**
 * LİSTE CEVABI — ÇALIŞMALAR ve BAYRAK BİRLİKTE.
 *
 * ### ⚠ KAPALILIK BİR TIKLAMAYLA ÖĞRENİLMEMELİ
 *
 * İlk yazımda ekran, kapalı kurulumu ancak kullanıcı "Günümü özetle"ye bastıktan sonra
 * dönen hatadan anlıyordu: kullanıcı olmayan bir özelliğe tıklıyor, sonra bölüm
 * kayboluyordu. Bayrak listeyle geliyor; ekran daha ilk karede doğru davranıyor. (Testte
 * de görüldü: tıklama + ağ hatası bekleyen kurgu, tam süitte zaman aşımına düşüyordu.)
 */
export type AiBriefingListResponse = {
  enabled: boolean;
  runs: AiBriefingRunSummary[];
};
